import React from 'react';
import { Alert, Button, Space, Tag, message } from 'antd';
import { MailOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { useInvoices } from '../../hooks';
import { DataTable } from '../../components';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

export const FailedPayments: React.FC = () => {
  const navigate = useNavigate();
  const {
    invoices,
    loading,
    error,
    meta,
    setFilters,
    refetch,
  } = useInvoices({ page: 1, limit: 20, status: 'failed' });

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters({ status: 'failed', page, limit: pageSize });
  };

  const handleContactTenant = (tenantId: string) => {
    navigate(`/tenants/${tenantId}`);
  };

  const handleManualRenew = async () => {
    message.info('Manual renew not yet implemented');
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Invoice #',
      dataIndex: 'id',
      key: 'id',
      width: 180,
    },
    {
      title: 'Tenant',
      dataIndex: 'tenantName',
      key: 'tenantName',
      render: (value, record) => (
        <Space direction="vertical" size={0}>
          <span>{value || 'Unknown tenant'}</span>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>{record.tenantId}</span>
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Failure Reason',
      dataIndex: 'failureReason',
      key: 'failureReason',
      width: 200,
      render: (reason: string) => reason ? (
        <Tag color="error">{reason}</Tag>
      ) : '-',
    },
    {
      title: 'Issued At',
      dataIndex: 'issuedAt',
      key: 'issuedAt',
      width: 130,
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD') : '-'),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 130,
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD') : '-'),
    },
  ];

  return (
    <div>
      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Failed to load failed payments"
          description={error.message}
          action={
            <Button size="small" onClick={refetch}>Retry</Button>
          }
        />
      )}

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="These tenants may be suspended soon due to payment failure. Please contact them or process manual renewal."
      />

      <DataTable
        title="Failed Payments"
        data={invoices}
        columns={columns}
        loading={loading}
        rowKey="id"
        onRefresh={refetch}
        emptyText="No failed payments found"
        pagination={{
          current: meta.page,
          pageSize: meta.limit,
          total: meta.total,
          onChange: handlePageChange,
        }}
        actionColumn={{
          width: 200,
          fixed: 'right',
          actions: (record) => [
            {
              key: 'contact',
              label: 'Contact Tenant',
              icon: <MailOutlined />,
              onClick: () => handleContactTenant(record.tenantId),
            },
            {
              key: 'renew',
              label: 'Manual Renew',
              icon: <ReloadOutlined />,
              onClick: handleManualRenew,
            },
          ],
        }}
      />
    </div>
  );
};

export default FailedPayments;